import { useEffect } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { useApi } from '@hero/context';
import type { UserCanUseAppAPI } from '@hero/api';
import { isLoggedIn } from './tokens';
import { useLogoutManager } from './useLogoutManager';
import { useNav } from '../navigation';

const STALE_TIME_MS = 30_000;
const REFETCH_INTERVAL_MS = 60_000;

export type UseCanUseAppGuardOptions = {
  /**
   * When `false`, poll and expose status only — no redirect / logout.
   * Used by mobile {@link BlockedAccessGate}.
   */
  navigate?: boolean;
};

/**
 * Polls `/auth/can-use-app` while the user is logged in and routes
 * to the appropriate full-screen gate when the backend reports a
 * block. The UI gate replaces server-side API cutoff — see
 * `apps/backend/src/app/auth/README.md` § Blocking.
 */
export function useCanUseAppGuard(options: UseCanUseAppGuardOptions = {}) {
  const { navigate: shouldNavigate = true } = options;
  const { t } = useTranslation(['toast']);
  const { logout } = useLogoutManager();
  const { navigate } = useNav();
  const api = useApi();

  const { data } = useQuery<UserCanUseAppAPI['data']>({
    queryKey: ['can-use-app'],
    queryFn: () => api.auth.canUseApp(undefined as never),
    staleTime: STALE_TIME_MS,
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    retry: false,
    enabled: isLoggedIn(),
  });

  useEffect(() => {
    if (!shouldNavigate || !data) return;
    if (data.status === 'active') return;

    if (data.status === 'blocked_by_user') {
      toast.error(data.message || t('toast:error.account_blocked'));
      navigate('account_blocked');
      return;
    }
    if (data.status === 'blocked_by_org') {
      toast.error(data.message || t('toast:error.org_blocked'));
      navigate('org_blocked', { orgName: data.orgName });
      return;
    }
    if (data.status === 'blocked_by_billing') {
      toast.error(data.message || t('toast:error.billing_blocked'));
      navigate('billing');
      return;
    }

    toast.error(data.message || t('toast:error.unauthorized'));
    logout();
  }, [data, logout, navigate, shouldNavigate, t]);

  return {
    status: data?.status,
    message: data?.message,
    orgName: data?.orgName,
  };
}
